import React from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { useAuth } from '../../../contexts/AuthContext';
import '../RouteGuard/RouteGuard.css';

/**
 * Persona-based route guard.
 *
 * Ensures the authenticated user's persona matches the required persona
 * for the route group. Must be rendered inside AuthProviderOutlet.
 *
 * @param {'admin'|'enduser'} persona - Required persona for this route group
 *
 * Behavior:
 *   - admin persona: requires isInternal (covers admin + super admin)
 *   - customer persona: requires isExternal
 *   - Wrong persona: redirects to the user's correct home page
 */
const VALID_PERSONAS = ['admin', 'enduser'];

const PersonaRoute = ({ persona }) => {
  const { isExternal, isLoading } = useAuth();

  if (
    process.env.NODE_ENV === 'development' &&
    !VALID_PERSONAS.includes(persona)
  ) {
    throw new Error(
      `PersonaRoute: invalid persona "${persona}". Expected one of: ${VALID_PERSONAS.join(', ')}`
    );
  }

  if (isLoading) {
    return (
      <div className="route-guard-loading">
        <CircularProgress />
      </div>
    );
  }

  const isInternal = !isExternal;
  const isAllowed = persona === 'admin' ? isInternal : isExternal;

  if (!isAllowed) {
    const redirectTo = isInternal ? '/admin/home' : '/enduser/home';
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export default PersonaRoute;
